// A standalone copy of NameChangeForm, registered as the `change_name` screen.
// It exists so the advanced name-change work can be built out here without
// touching the live Name Change screen, which stays exactly as it is until the
// new one is finished.
//
// Behaviour is currently identical to NameChangeForm, audit changeType included
// ('NAME_CHANGE'), so the two are interchangeable today. Divergence goes here.
import React, { useEffect, useState } from 'react';
import { CheckCircle, X, Check, ArrowRight, Info } from '@phosphor-icons/react';
import TicketSelector from './TicketSelector';
import {
  submitNameChange, saveData, getSubscriberNames, getSubscriberNameDetails,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, isEmptyResult, inlineNoticeColor } from '../utils/noData';

const onlyLetters = (value) => value.replace(/[^A-Za-z ]/g, '');

function ChangeNameForm({ connectionId }) {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [record, setRecord] = useState(null);
  // Branch / group / ticket behind the loaded record — getnames is keyed on them.
  const [selection, setSelection] = useState(null);
  const [subscriberName, setSubscriberName] = useState('');
  const [chitRows, setChitRows] = useState([]);
  const [chitsLoading, setChitsLoading] = useState(false);
  const [chitsError, setChitsError] = useState('');
  // One chit at a time: the checkbox column behaves as a radio, so the old-name
  // fields below can only ever describe a single row.
  const [selectedChitKey, setSelectedChitKey] = useState(null);
  const [newName, setNewName] = useState('');
  const [newSurname, setNewSurname] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOldFullName, setPendingOldFullName] = useState('');
  const [pendingNewFullName, setPendingNewFullName] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  // getnames resolves the subscriber behind the chosen ticket; its contact id then
  // drives getnamesdetials, which lists every chit that contact holds. Both are
  // cleared whenever the ticket selection goes away.
  useEffect(() => {
    if (!selection) {
      setSubscriberName('');
      setChitRows([]);
      setSelectedChitKey(null);
      setChitsError('');
      return undefined;
    }
    // Selections can change faster than these two calls return, so a superseded
    // response is dropped rather than left to overwrite the current one.
    let cancelled = false;
    setChitsLoading(true);
    setChitsError('');
    setSelectedChitKey(null);
    (async () => {
      try {
        const names = await getSubscriberNames(
          selection.branchCode, selection.groupCode, selection.ticketNo,
        );
        if (cancelled) return;
        const first = Array.isArray(names) ? names[0] : null;
        setSubscriberName(first?.subscriber_name || '');
        if (!first?.contact_id) {
          setChitRows([]);
          setChitsError(NO_DATA_MESSAGE);
          return;
        }
        const details = await getSubscriberNameDetails(first.contact_id);
        if (cancelled) return;
        setChitRows(Array.isArray(details) ? details : []);
        // Inline, next to the empty grid, rather than a toast: this runs off the
        // TicketSelector's own load, which already reports its failures inline.
        if (isEmptyResult(details)) setChitsError(NO_DATA_MESSAGE);
      } catch (err) {
        if (cancelled) return;
        setSubscriberName('');
        setChitRows([]);
        setChitsError('Could not load the subscriber details for that ticket.');
      } finally {
        if (!cancelled) setChitsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selection]);

  // A row is identified by the chit it represents, not by its array index, so the
  // tick survives the list being reloaded.
  const chitKey = (row) => `${row.contact_id}-${row.groupcode}-${row.ticketno}`;
  const selectedChit = chitRows.find((r) => chitKey(r) === selectedChitKey) || null;

  // The old-name fields describe the ticked chit. Every row here belongs to the
  // same contact and these three values live on the contact, so they come from the
  // loaded record — but they stay blank until a row is actually ticked.
  const active = selectedChit ? record : null;

  const newMailingName = `${newName} ${newSurname}`.trim();

  const nameInvalid = touched && !newName;
  const surnameInvalid = touched && !newSurname;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!record && !!selectedChit && !!newName && !!newSurname
    && reason.trim().length >= 10 && reason.length <= 200;

  const showToast = (severity, summary, detail, sticky = false) => {
    setToast({ severity, summary, detail, sticky });
    if (!sticky) setTimeout(() => setToast(null), 4000);
  };

  /** Step 1: validate, stage the pending change, open the confirm modal. */
  const confirmSave = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    setPendingOldFullName(`${record.oldName} ${record.oldSurname}`.trim());
    setPendingNewFullName(`${newName} ${newSurname}`.trim());
    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after the user clicks "Yes, Save" in the modal. */
  const doSave = async () => {
    setSaving(true);
    try {
      await submitNameChange(connectionId, {
        contactId: record.contactId,
        branchCode: record.branchCode,
        oldName: record.oldName,
        oldSurname: record.oldSurname,
        oldMailingName: record.oldMailingName,
        newName,
        newSurname,
        reason,
      });
      try {
        await saveData([{
          schemaName: record.branchCode ?? null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'NAME_CHANGE',
          oldData: {
            contactId: record.contactId,
            branchCode: record.branchCode,
            name: record.oldName,
            surname: record.oldSurname,
            mailingName: record.oldMailingName,
          },
          newData: {
            contactId: record.contactId,
            branchCode: record.branchCode,
            name: newName,
            surname: newSurname,
            mailingName: newMailingName,
          },
          reason: reason.trim(),
        }]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', `Name changed from ${pendingOldFullName} to ${pendingNewFullName}.`, true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Name updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (e) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Save Failed', e?.response?.data?.detail || 'Could not save this change. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setRecord(null);
    setNewName('');
    setNewSurname('');
    setReason('');
    setTouched(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="change-name-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="change-name-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
        <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>Name Change</div>

        <TicketSelector
          key={formKey}
          connectionId={connectionId}
          onRecordChange={setRecord}
          onSelectionChange={setSelection}
        />

        {/* Subscriber behind the chosen ticket, and every chit that subscriber
            holds. Ticking one is what fills in the current name details below. */}
        {selection && (
          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Subscriber Name
            </label>
            <input
              className="input-field"
              style={{ fontSize: '13px', background: '#F9FAFB' }}
              value={chitsLoading ? 'Loading...' : subscriberName}
              readOnly
              placeholder="—"
              data-testid="change-name-subscriber"
            />

            {chitsError && (
              <div style={{ fontSize: '11px', color: inlineNoticeColor(chitsError), marginTop: '6px' }} data-testid="change-name-chits-error">
                {chitsError}
              </div>
            )}

            {!chitsError && (
              <div style={{ marginTop: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', overflowX: 'auto', width: 0, minWidth: '100%' }}>
                <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }} data-testid="change-name-chits-table">
                  <thead style={{ background: '#F9FAFB' }}>
                    <tr>
                      <th style={{ ...{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }, width: '44px' }} />
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>Contact ID</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>Subscriber Name</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>Group Code</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>Ticket No</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap' }}>Chit Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chitsLoading && (
                      <tr>
                        <td colSpan={6} style={{ ...{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }, textAlign: 'center', color: '#9CA3AF' }}>
                          Loading subscriber details...
                        </td>
                      </tr>
                    )}
                    {!chitsLoading && chitRows.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ ...{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }, textAlign: 'center', color: '#9CA3AF' }} data-testid="change-name-chits-empty">
                          No chits found for this subscriber.
                        </td>
                      </tr>
                    )}
                    {!chitsLoading && chitRows.map((row) => {
                      const key = chitKey(row);
                      const checked = selectedChitKey === key;
                      return (
                        <tr
                          key={key}
                          style={{ background: checked ? '#EFF6FF' : 'white' }}
                          data-testid={`change-name-chit-row-${row.ticketno}`}
                        >
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              // Only one chit can describe the name being changed, so
                              // ticking a row replaces the previous tick rather than
                              // adding to it.
                              onChange={() => setSelectedChitKey(checked ? null : key)}
                              style={{ width: '15px', height: '15px', accentColor: '#002FA7', cursor: 'pointer' }}
                              data-testid={`change-name-chit-check-${row.ticketno}`}
                            />
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.contact_id}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.subscriber_name}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.groupcode}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.ticketno}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #F3F4F6', color: '#111827', fontSize: '12px', whiteSpace: 'nowrap' }}>{row.chit_status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!chitsLoading && chitRows.length > 0 && !selectedChit && (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '6px' }}>
                Tick one chit to load its current name details below.
              </div>
            )}
          </div>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!selectedChit} hint="Select a ticket and tick a chit in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Update Name Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Review current details on the left, enter new details on the right</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>Current Details (Read-Only)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={active?.oldName || ''} readOnly data-testid="change-name-old-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Surname</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={active?.oldSurname || ''} readOnly />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Mailing Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={active?.oldMailingName || ''} readOnly />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Status</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={active?.oldStatus || ''} readOnly />
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '8px' }}>New Details (Editable)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Name *</label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: nameInvalid ? '#DC2626' : undefined }}
                  value={newName}
                  maxLength={50}
                  placeholder="Enter new name"
                  onChange={(e) => setNewName(onlyLetters(e.target.value))}
                  data-testid="change-name-new-name"
                />
                {nameInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Name is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Surname *</label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: surnameInvalid ? '#DC2626' : undefined }}
                  value={newSurname}
                  maxLength={20}
                  placeholder="Enter new surname"
                  onChange={(e) => setNewSurname(onlyLetters(e.target.value))}
                  data-testid="change-name-new-surname"
                />
                {surnameInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Surname is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Mailing Name (Auto)</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={newMailingName} readOnly data-testid="change-name-new-mailing" />
              </div>
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!selectedChit || !newName || !newSurname} hint="Enter the new name and surname in section B first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Explain why this name change is being made..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="change-name-reason"
        />
        <div style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
          {reason.length} / 200
        </div>
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626' }}>Minimum 10 characters required</div>}
        </SectionLock>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="change-name-clear-button">Clear</button>
          <button type="button" onClick={confirmSave} className="btn-primary" disabled={saving} data-testid="change-name-save-button">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="change-name-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle size={18} color="#002FA7" weight="bold" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Name Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to change this subscriber&apos;s name. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>From</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldFullName || '—'}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>To</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewFullName || '—'}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="change-name-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="change-name-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangeNameForm;